import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import { Form } from 'react-bootstrap'
import Row from 'react-bootstrap/Row'
import theme from '../constants/theme'

export const waveAnim = keyframes`
  0%   { transform: scaleY(0.3); }
  100% { transform: scaleY(1); }
`

export const PageBackground = styled('div')({
  backgroundColor: '#F1F5FA',
  minHeight: '100vh',
  overflowX: 'clip',
})

export const StickyHeader = styled(Row)({
  position: 'sticky',
  top: 0,
  zIndex: 1000,
  backgroundColor: theme.white,
  padding: '0px 20px',
})

export const MainContent = styled('div')({
  padding: '24px 24px 32px',
  display: 'flex',
  justifyContent: 'center',
})

export const LeftPanelCard = styled('div')({
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  overflow: 'hidden',
  boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
})

export const CardTopBar = styled('div')({
  padding: '14px 20px',
  borderBottom: '1px solid #f0f2f5',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
})

export const ProgressDashes = styled('div')({
  display: 'flex',
  gap: '4px',
  alignItems: 'center',
})

export const ProgressDash = styled('div')(({ active }) => ({
  height: '4px',
  width: '22px',
  borderRadius: '2px',
  backgroundColor: active ? theme.talentinoBlue : '#D9E2EC',
  opacity: active ? 1 : 0.5,
}))

export const QuestionArea = styled('div')({
  padding: '20px',
})

export const MetaRow = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '14px',
})

export const QuestionBadge = styled('span')({
  backgroundColor: '#E8F1FB',
  color: theme.talentinoBlue,
  fontSize: '12px',
  fontWeight: '600',
  padding: '4px 12px',
  borderRadius: '20px',
})

export const AnswerStatusText = styled('span')(({ answered }) => ({
  fontSize: '13px',
  fontWeight: '500',
  color: answered ? theme.talentinoGreen : theme.talentinoGray,
}))

export const QuestionTitle = styled('h2')({
  fontSize: '18px',
  fontWeight: '700',
  color: theme.hintsNotifications,
  lineHeight: '1.5',
  marginBottom: '16px',
})

export const TextareaContainer = styled('div')({
  position: 'relative',
  marginBottom: '4px',
})

export const AnswerTextarea = styled(Form.Control)({
  border: '1px solid #DDE3EC',
  borderRadius: '10px',
  padding: '12px 14px',
  paddingBottom: '28px',
  fontSize: '14px',
  resize: 'vertical',
  color: theme.hintsNotifications,
  outline: 'none',
  boxShadow: 'none',
})

export const WordCountLabel = styled('span')({
  position: 'absolute',
  bottom: '10px',
  right: '12px',
  fontSize: '12px',
  color: '#aaa',
  pointerEvents: 'none',
})

export const VoiceBar = styled('div')({
  marginTop: '12px',
  backgroundColor: '#F1F5FA',
  borderRadius: '10px',
  padding: '10px 14px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
})

export const StopListenBtn = styled('button')({
  display: 'flex',
  alignItems: 'center',
  gap: '7px',
  backgroundColor: '#A93C38',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  padding: '8px 14px',
  fontSize: '13px',
  fontWeight: '600',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  flexShrink: 0,
})

export const StartListenBtn = styled('button')({
  display: 'flex',
  alignItems: 'center',
  gap: '7px',
  backgroundColor: theme.talentinoBlue,
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  padding: '8px 14px',
  fontSize: '13px',
  fontWeight: '600',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  flexShrink: 0,
})

export const WaveformContainer = styled('div')({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '3px',
  height: '32px',
})

export const WaveformBar = styled('div')(
  ({ listening, animDuration, barHeight }) => ({
    width: '3px',
    height: `${listening ? barHeight : 3}px`,
    backgroundColor: listening ? '#5B9BD5' : '#3A4560',
    borderRadius: '2px',
    transformOrigin: 'center',
    animation: listening
      ? `${waveAnim} ${animDuration}s ease-in-out infinite alternate`
      : 'none',
    transition: 'height 0.2s ease, background-color 0.2s',
  }),
)

export const VoiceHint = styled('span')({
  color: '#8A97B0',
  fontSize: '12px',
  whiteSpace: 'nowrap',
  flexShrink: 0,
})

export const NavActions = styled('div')({
  marginTop: '20px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
})

export const SubmitBtn = styled('button')(({ canSubmit }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  backgroundColor: canSubmit
    ? theme.talentinoBlue
    : theme.defaultDisabledBorderColor,
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  padding: '9px 22px',
  fontSize: '14px',
  fontWeight: '600',
  cursor: canSubmit ? 'pointer' : 'not-allowed',
}))

// ── Right sidebar ──────────────────────────────────────────────────────────────

export const TimerCard = styled('div')({
  backgroundColor: '#fff',
  borderRadius: '14px',
  padding: '16px 18px',
  marginBottom: '12px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
})

export const SectionLabel = styled('div')(({ mb = '0px' }) => ({
  fontSize: '11px',
  fontWeight: '700',
  color: '#8A97B0',
  letterSpacing: '1px',
  textTransform: 'uppercase',
  marginBottom: mb,
}))

export const TimerRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  justifyContent: 'center',
})

export const TimeRemainingText = styled('div')({
  fontSize: '14px',
  fontWeight: '600',
  color: theme.hintsNotifications,
})

export const TimeTotalText = styled('div')({
  fontSize: '40px',
  color: theme.talentinoBlue,
  marginTop: '2px',
  fontWeight: '600',
  textAlign: 'center',
})

export const ProgressCard = styled('div')({
  backgroundColor: '#fff',
  borderRadius: '14px',
  padding: '16px 18px',
  marginBottom: '12px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
  height: '50%',
  overflowY: 'auto',
})

export const ProgressCardHeader = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '12px',
})

export const ProgressCount = styled('span')({
  fontSize: '13px',
  fontWeight: '700',
  color: theme.talentinoBlue,
})

export const QuestionList = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
})

export const QuestionListItem = styled('div')(({ isCurrent }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '8px 10px',
  borderRadius: '8px',
  backgroundColor: isCurrent ? '#EBF3FB' : 'transparent',
}))

export const StatusCircleDone = styled('div')({
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  backgroundColor: '#E6F7EF',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
})

export const StatusCircle = styled('div')(({ isCurrent }) => ({
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  backgroundColor: isCurrent ? theme.talentinoBlue : '#E8EDF3',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  fontSize: '11px',
  fontWeight: '700',
  color: isCurrent ? '#fff' : theme.talentinoDarkerGray,
}))

export const QuestionItemLabel = styled('span')(({ status }) => ({
  flex: 1,
  fontSize: '13px',
  fontWeight: status === 'current' ? '600' : '400',
  color:
    status === 'upcoming'
      ? theme.talentinoDarkerGray
      : theme.hintsNotifications,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}))

export const StatusBadgeLabel = styled('span')(({ status }) => ({
  fontSize: '11px',
  fontWeight: '500',
  color:
    status === 'done'
      ? theme.talentinoGreen
      : status === 'current'
        ? theme.talentinoBlue
        : '#aaa',
  flexShrink: 0,
}))

export const AutoSaveBox = styled('div')({
  backgroundColor: '#EFF6FF',
  borderRadius: '10px',
  padding: '12px 14px',
  display: 'flex',
  gap: '10px',
  alignItems: 'flex-start',
})

export const AutoSaveText = styled('p')({
  margin: 0,
  fontSize: '12px',
  color: theme.talentinoDarkerGray,
  lineHeight: '1.6',
})
